import { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import WatchlistDashboardService, { DashboardHeader as HeaderData, InstituteContext } from '../WatchlistDashboardService';
import HitlService from '../../../watchlists/hitl/HitlService';
import SessionContext from '../../../../library/Core/SessionContext';

/**
 * Stage 6 — Dashboard Header. Institute identity + a campus filter (for groups) + the 6-KPI strip.
 * KPIs aggregate across all prior layers; institute_id is group-scoped, an optional
 * filter_institute_id narrows to one campus. `onFilterChange` lifts the campus selection so the
 * whole dashboard re-fetches under the same filter.
 */
const DashboardHeader = ({
    instituteId,
    filterInstituteId,
    onFilterChange,
}: {
    instituteId: string | number;
    filterInstituteId: string | null;
    onFilterChange: (id: string | null) => void;
}) => {
    const { sessionState } = useContext(SessionContext);
    const isInstituteAdmin = sessionState?.userTypeCode === 'INSTITUTE_ADMIN';

    const [context, setContext] = useState<InstituteContext | null>(null);
    const [header, setHeader] = useState<HeaderData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [hitlPending, setHitlPending] = useState<number | null>(null);

    useEffect(() => {
        WatchlistDashboardService.getInstituteContext(instituteId)
            .then(setContext)
            .catch(() => setContext(null));
    }, [instituteId]);

    useEffect(() => {
        setLoading(true);
        setError(null);
        WatchlistDashboardService.getDashboardHeader(instituteId, filterInstituteId)
            .then(setHeader)
            .catch((err) => setError(err?.message || 'Failed to load dashboard summary.'))
            .finally(() => setLoading(false));

        HitlService.getSummary(instituteId, filterInstituteId)
            .then((s) => setHitlPending(s.pending_count))
            .catch(() => setHitlPending(null));
    }, [instituteId, filterInstituteId]);

    const hitlQueueLink = `/support/hitl-review-queue?institute_id=${instituteId}${filterInstituteId ? `&filter_institute_id=${filterInstituteId}` : ''}`;

    return (
        <div className="col-12">
            <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-2 mb-3">
                <div>
                    <h2 className="m-0 font-24 font-600">Alumni Watchlist Engine</h2>
                    <div className="mt-1 d-flex align-items-center gap-2 flex-wrap">
                        {context
                            ? <>
                                <span className="font-600 font-15 text-dark-blue">
                                    {context.is_group && context.institute_group_name
                                        ? context.institute_group_name
                                        : context.institute_name}
                                </span>
                                {context.is_group && (
                                    <span className="badge rounded-pill text-bg-primary font-10 fw-semibold px-2 py-1">Group</span>
                                )}
                                {/* {context.is_group && context.institute_name && (
                                    <span className="font-12 text-muted">({context.institute_name})</span>
                                )} */}
                              </>
                            : <span className="font-14 text-secondary-emphasis">A live map of alumni influence</span>}
                    </div>
                </div>

                {/* Campus filter — only for groups with >1 institute */}
                {context && context.is_group && context.campuses.length > 1 && (
                    <div className="d-flex align-items-center gap-2">
                        <label className="font-12 font-600 text-muted m-0">Campus</label>
                        <select
                            className="form-select form-select-sm rounded-3 font-13"
                            style={{ minWidth: 200 }}
                            value={filterInstituteId ?? ''}
                            onChange={(e) => onFilterChange(e.target.value || null)}>
                            <option value="">All campuses (group)</option>
                            {context.campuses.map((c) => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            {error && !loading && <div className="alert alert-danger font-13">{error}</div>}

            {/* KPI strip */}
            <div className="row g-3 row-cols-2 row-cols-md-3 row-cols-xl-6">
                {(loading || !header
                    ? Array.from({ length: 6 }).map((_, i) => ({ key: `s${i}`, label: '', icon: '', value: 0, sublabel: '', pct_of_total: null }))
                    : header.cards
                ).map((card) => (
                    <div className="col" key={card.key}>
                        <div className="bg-white border rounded-4 p-3 h-100 d-flex flex-column gap-1 shadow-sm">
                            <div className="d-flex align-items-center justify-content-between">
                                <span className="font-11 font-600 text-uppercase letter-spacing-1 text-muted">{card.label || '—'}</span>
                                <span className="font-16">{card.icon}</span>
                            </div>
                            <div className="font-26 font-700 text-dark-blue">
                                {loading || !header ? '…' : card.value.toLocaleString()}
                            </div>
                            <div className="font-11 text-secondary d-flex align-items-center gap-2">
                                <span>{card.sublabel}</span>
                                {card.pct_of_total != null && (
                                    <span className="badge bg-primary bg-opacity-10 text-primary font-10">{card.pct_of_total}%</span>
                                )}
                            </div>
                        </div>
                    </div>
                ))}

                {/* HITL Pending KPI — links to the review queue (spec §8.4) — hidden for INSTITUTE_ADMIN */}
                {!isInstituteAdmin && (
                <div className="col">
                    <Link to={hitlQueueLink} className="text-decoration-none">
                        <div className="bg-white border rounded-4 p-3 h-100 d-flex flex-column gap-1 shadow-sm border-warning border-opacity-50">
                            <div className="d-flex align-items-center justify-content-between">
                                <span className="font-11 font-600 text-uppercase letter-spacing-1 text-muted">HITL Pending</span>
                                <span className="font-16">🕐</span>
                            </div>
                            <div className="font-26 font-700 text-dark-blue">{hitlPending == null ? '…' : hitlPending.toLocaleString()}</div>
                            <div className="font-11 text-secondary">View HITL</div>
                        </div>
                    </Link>
                </div>
                )}
            </div>
        </div>
    );
};

export default DashboardHeader;
